import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Loader } from 'lucide-react';
import { useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';

const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

function CheckoutForm({
  bookingId,
  amountEur,
  customerEmail,
  customerName,
  onSuccess,
  onError,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.PAYMENT_CREATE_INTENT), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId,
          amount: amountEur,
          customerEmail,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'No se pudo iniciar el pago');
      }
      const { clientSecret } = await res.json();
      const card = elements.getElement(CardElement);
      const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card,
          billing_details: {
            name: customerName || customerEmail,
            email: customerEmail,
          },
        },
      });
      if (stripeErr) throw new Error(stripeErr.message);
      if (paymentIntent?.status !== 'succeeded') {
        throw new Error('El pago no se completó');
      }
      const confirmRes = await fetch(buildApiUrl(API_ENDPOINTS.PAYMENT_CONFIRM), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId,
          paymentIntentId: paymentIntent.id,
          temporaryBlockIds: [],
        }),
      });
      if (!confirmRes.ok) {
        const err = await confirmRes.json().catch(() => ({}));
        throw new Error(err.message || 'No se pudo registrar el pago en el servidor');
      }
      await confirmRes.json();
      onSuccess?.();
    } catch (err) {
      const m = err?.message || 'Error al procesar el pago';
      setMsg(m);
      onError?.(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="edit-booking-stripe-form">
      <div className="edit-stripe-card-wrap">
        <CardElement
          options={{
            style: {
              base: { fontSize: '16px', color: '#111827', '::placeholder': { color: '#9ca3af' } },
            },
          }}
        />
      </div>
      {msg && (
        <p className="edit-form-hint" style={{ color: '#b91c1c', marginTop: '8px' }}>
          {msg}
        </p>
      )}
      <button type="submit" className="btn-primary" disabled={!stripe || busy} style={{ marginTop: '12px' }}>
        {busy ? (
          <>
            <Loader size={16} /> Procesando…
          </>
        ) : (
          `Cobrar con tarjeta (${amountEur.toFixed(2)} €)`
        )}
      </button>
    </form>
  );
}

/**
 * Cobro del saldo pendiente con Stripe (mismo flujo público: create-intent + confirm).
 */
export default function EditBookingBalanceStripe({
  enabled,
  bookingId,
  amountEur,
  customerEmail,
  customerName,
  onSuccess,
  onError,
}) {
  if (!enabled || amountEur <= 0) return null;

  if (!stripePromise) {
    return (
      <p className="edit-form-hint">
        {/* Define <code>VITE_STRIPE_PUBLISHABLE_KEY</code> en el entorno del admin para cobrar con tarjeta desde aquí. */}
      </p>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        bookingId={bookingId}
        amountEur={amountEur}
        customerEmail={customerEmail}
        customerName={customerName}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
}
